import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as PR from "../../../prime-react/index";
import "./MyAnalytics.scss";
import Layout from '../../../layouts/Layout';
import MenuBar from '../../../layouts/MenuBar';
import { useDispatch, useSelector } from 'react-redux';
import { getAlertsCount, getLocations, getSports } from '../../../services/api';
import moment from 'moment';

import MyAnalyticsGraph from './MyAnalyticsGraph';
import { resetMemberDateRange, resetMemberPlayerDateRange, resetMemberMarketDateRange, storeMemberDateRange, storeMemberPlayerDateRange, storeMemberMarketDateRange } from '../../../store/filter.slice';
import { areDateRangesEqual, defaultDateRange } from '../../../utils/reuse';

const MyAnalytics = () => {
    const sessionData = useSelector(state => state.auth.sessionData);
    const memberAnalyticsState = useSelector(state => state.memberAnalyticsFilter);
    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const dispatch = useDispatch();
    const [dates, setDates] = useState(memberAnalyticsState.memberDateRange);
    const [keyMetrics, setKeyMetrics] = useState(null);
    const [sports, setSports] = useState([]);
    const [location, setLocation] = useState([]);
    const [playerDates, setPlayerDates] = useState(memberAnalyticsState.memberPlayerDateRange);
    const [marketDates, setMarketDates] = useState(memberAnalyticsState.memberMarketDateRange);
    //get key metric data
    const getMetricHandler = useCallback(() => {
        const obj = {
            type: "rrate,ar,art,tsar",
        };
        if (dates && dates?.length > 0) {
            const startDate = moment(dates[0]).format('YYYY-MM-DD');
            const endDate = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : startDate;
            obj.startDate = startDate;
            obj.endDate = endDate;
        }
        getAlertsCount(obj, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                setKeyMetrics(responseData);
            }
        })
    }, [dispatch, headers, dates]);

    // Get list of all the sports
    const getSportsHandler = useCallback(() => {
        getSports(null, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const sports = responseData;
                const result = (sports?.length > 0) ? sports?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setSports(result);
            } else {
                setSports([]);
            }
        })
    }, [dispatch, headers]);

    // Get list of all the locations
    const getLocationHandler = useCallback(() => {
        getLocations(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const locations = responseData;
                const result = (locations?.length > 0) ? locations?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setLocation(result);
            } else {
                setLocation([]);
            }
        })
    }, [dispatch, headers]);

   
    useEffect(() => {
        getMetricHandler();
        getSportsHandler();
        getLocationHandler();
    }, [getMetricHandler, getSportsHandler, getLocationHandler])
    useEffect(() => {
        setDates(memberAnalyticsState.memberDateRange);
    }, [memberAnalyticsState.memberDateRange]);
    useEffect(() => {
        setPlayerDates(memberAnalyticsState.memberPlayerDateRange);
    }, [memberAnalyticsState.memberPlayerDateRange]);
    useEffect(() => {
        setMarketDates(memberAnalyticsState.memberMarketDateRange);
    }, [memberAnalyticsState.memberMarketDateRange]);
    const setPlayerDateHandler = (value) => {
        if (value === "remove") {
            dispatch(resetMemberPlayerDateRange())
        } else {
            dispatch(storeMemberPlayerDateRange(value))
        }
    }
    const setMarketDateHandler = (value) => {
        if (value === "remove") {
            dispatch(resetMemberMarketDateRange())
        } else {
            dispatch(storeMemberMarketDateRange(value))
        }
    }
    const calenderHandler = (value) => {
        dispatch(storeMemberDateRange(value))
    }
    const resetDateHandler = () => {
        dispatch(resetMemberDateRange())
    }
    const isDefaultDateRange = areDateRangesEqual(dates, defaultDateRange);
    
    return (
        <>
            <Layout>
                <MenuBar />
                <section className="my-analytics-tab">
                    <div className="m-0 flex align-items-center justify-content-between">
                        <div className='heading-card'> 
                            <h1>Key Metrics</h1>
                        </div>
                        <div className="card custom-clear-calendar">
                            <PR.Calendar
                                value={dates}
                                onChange={(e) => calenderHandler(e.value)}
                                selectionMode="range"
                                iconPos="left"
                                readOnlyInput
                                showIcon
                                className='date-calendar'
                                placeholder='Date'
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                            />
                            {!isDefaultDateRange &&
                                <div className='custom-clear'>
                                    <i className="pi pi-times text-sm" onClick={() => resetDateHandler()}></i>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="analytics-wrapper">
                        <div className="grid">
                            <div className="col-6">
                                <div className="info-box combined-box">
                                    <div className="flex align-items-start">
                                        <div className="pr-2">
                                            <h4>Totals of All Groups</h4>
                                            <p>Total alerts raised by all groups in the given date range</p>
                                        </div>
                                    </div>

                                    <div className="combined-metrics">
                                        <div className="metric-item">
                                            <span className="metric-label">Total Alerts</span>
                                            <h2 className="metric-value">
                                                {keyMetrics?.suspiciousAlertRate?.[0]?.alertCount ?? 0}
                                            </h2>
                                        </div>

                                        <div className="metric-divider"></div>

                                        <div className="metric-item">
                                            <span className="metric-label">Suspicious Alerts</span>
                                            <h2 className="metric-value">
                                                {keyMetrics?.suspiciousAlertRate?.[0]?.suspiciousCount ?? 0}
                                            </h2>
                                        </div>

                                        <div className="metric-divider"></div>

                                        <div className="metric-item">
                                            <span className="metric-label">Suspicious Rate</span>
                                            <h2 className="metric-value">
                                                {keyMetrics?.suspiciousAlertRate?.[0]?.suspiciousRate
                                                    ? keyMetrics.suspiciousAlertRate[0].suspiciousRate.toFixed(1)
                                                    : 0}%
                                            </h2>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-6">
                                <div className="info-box combined-box">
                                    <div className="flex align-items-start">
                                        <div className="pr-2">
                                            <h4>Totals of your organization</h4>
                                            <p>Total alerts raised by your organization in the given date range</p>
                                        </div>
                                    </div>

                                    <div className="combined-metrics">
                                        <div className="metric-item">
                                            <span className="metric-label">Total Alerts</span>
                                            <h2 className="metric-value">
                                                {keyMetrics?.alertsRaisedPerMember?.[0]?.alertCount ?? 0}
                                            </h2>
                                        </div>

                                        <div className="metric-divider"></div>

                                        <div className="metric-item">
                                            <span className="metric-label">Suspicious Alerts</span>
                                            <h2 className="metric-value">
                                                {keyMetrics?.alertsRaisedPerMember?.[0]?.suspiciousCount ?? 0}
                                            </h2>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-6">
                                <div className="info-box">
                                    <div className="flex align-items-start">
                                        <div className='pr-2'>
                                            <h4>Response Rate</h4>
                                            <p>The % of alerts responded by your organisation in a given date range</p>
                                        </div>
                                    </div>
                                    <div className="flex mt-3 align-items-center">
                                        <h2>{keyMetrics?.responseRatePerMember ? keyMetrics?.responseRatePerMember[0]?.responseRate?.toFixed(1) : 0}%</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="info-box">
                                    <div className="flex align-items-start">
                                        <div className='pr-2'>
                                            <h4>Average Response Time</h4>
                                            <p>The average time it takes your organisation to respond to an alert in a given date range</p>
                                        </div>
                                    </div>
                                    <div className="flex mt-3 align-items-center justify-content-between">
                                        <h2>{keyMetrics?.averageResponseTimePerMember ? ((keyMetrics?.averageResponseTimePerMember[0]?.avg_response_time_seconds) / 3600).toFixed(1) : 0} hrs</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="hr-line"></div>
                        <div className="grid mt-2">
                            {/* Alerts Raised by Sport */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'arps'} graphColor={'#FFCD02'} secondGraphColor={'#ba3d40'} responseKey={'alertsRaisedPerSport'} graphCountKey={'alertCount'} graphNameKey={'sportName'} secondGraphCountKey={'suspiciousCount'} optionsData={sports} placeholder={'Select sports'} dates={dates}></MyAnalyticsGraph>

                            </div>
                            {/* Response Rate by Sport */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'rrps'} graphColor={'#FFCD02'} responseKey={'responseRatePerSport'} graphCountKey={'responseRate'} graphNameKey={'sportName'} optionsData={sports} placeholder={'Select sports'} dates={dates}></MyAnalyticsGraph>
                            </div>
                            {/* Response Type */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'rtps'} graphColor={'#FFCD02'} responseKey={'responseTypePerSport'} graphCountKey={'responseRate'} graphNameKey={'responseType'} optionsData={sports} placeholder={'Select sport'} dates={dates}></MyAnalyticsGraph>
                            </div>

                        </div>
                        <div className="grid mt-1">
                            {/* Total Alerts by Sport */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'taps'} graphColor={'#FFCD02'} secondGraphColor={'#ba3d40'}  responseKey={'totalAlertsPerSport'} graphCountKey={'alertCount'} graphNameKey={'sportName'} secondGraphCountKey={'suspiciousCount'} optionsData={sports} placeholder={'Select sports'} dates={dates}></MyAnalyticsGraph>
                            </div>
                            {/* Total Alerts by Location */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'tapl'} graphColor={'#FFCD02'} responseKey={'totalAlertsPerLocation'} graphCountKey={'alertCount'} graphNameKey={'locationName'} optionsData={location} placeholder={'Select locations'} dates={dates}></MyAnalyticsGraph>

                            </div>
                            {/* Tennis Players of Concern */}
                            <div className="col-4">
                                <MyAnalyticsGraph graphType={'tpc'} graphColor={'#FFCD02'} responseKey={'tennisPlayersOfConcern'} graphCountKey={'tagCount'} graphNameKey={'tagName'} optionsData={[]} placeholder={''} dates={dates}></MyAnalyticsGraph>
                            </div>
                        </div>

                        <div className="hr-line"></div>
                        
                        {/* Athletes/Teams of Concern */}
                        <div className="grid mt-1">
                            <div className="col-12">
                                <MyAnalyticsGraph graphType={'pcps'} graphColor={'#FFCD02'} responseKey={'playersOfConcernPerSport'} graphCountKey={'tagCount'} graphNameKey={'tagName'} optionsData={sports} placeholder={'Select Sports'} dates={playerDates} isPlayer={true} isDetailScreen={true} setPlayerDateHandler={setPlayerDateHandler}></MyAnalyticsGraph>
                            </div>
                        </div>

                        {/* Markets of Concern */}
                        <div className="grid mt-1">
                            <div className="col-12">
                                <MyAnalyticsGraph graphType={'mcps'} graphColor={'#FFCD02'} responseKey={'marketsOfConcernPerSport'} graphCountKey={'tagCount'} graphNameKey={'tagName'} optionsData={sports} placeholder={'Select Sports'} dates={marketDates} isPlayer={true} isDetailScreen={true} setPlayerDateHandler={setMarketDateHandler} locations={location}></MyAnalyticsGraph>
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>

        </>
    );
};

export default MyAnalytics;